import { makeAutoObservable, runInAction } from 'mobx'

import { BeatApi } from '../api'
import type { Beat, Offer } from '../types'
import { orderArray, transformOfferIntoBeatLikeObject } from '../utils/BeatUtils'

import { GlobalStore } from './GlobalStore'

export class BeatStore {
  globalStore: GlobalStore
  api: BeatApi = new BeatApi('/', '')
  beats: Beat[] = []
  isLoading = false

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.api = new BeatApi(globalStore.endpoint, globalStore.sessionToken, globalStore.onError)

    makeAutoObservable(this)
  }

  loadBeats = async () => {
    this.isLoading = true
    try {
      const beats = await this.api.getBeats()
      const offers = await this.api.getOffers()
      const beatLikeOffers = offers.map((o: Offer) => transformOfferIntoBeatLikeObject(o))

      runInAction(() => {
        this.beats = orderArray(beats, beatLikeOffers, 'active_at', 'desc')
      })
    } catch (error) {
      console.error('Error occurred while loading beats:', error)
      this.globalStore.captureException(error, { store: 'BeatStore' })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  getFilteredBeats(filter: { categoryGuids?: string[]; templates?: string[] }): Beat[] {
    const { categoryGuids, templates } = filter
    let filteredBeats = this.beats
    if (categoryGuids) filteredBeats = this.filterBeatsByCategory(filteredBeats, categoryGuids)
    if (templates) filteredBeats = this.filterBeatsByTemplate(filteredBeats, templates)
    return filteredBeats
  }

  // Return beats with any beat.category_guids that are in categoryGuids
  filterBeatsByCategory(beats: Beat[], categoryGuids: string[]): Beat[] {
    return beats.filter((beat) => {
      return beat.category_guids?.some((guid) => categoryGuids.includes(guid))
    })
  }

  // Return beats with a beat.template that is in templates
  filterBeatsByTemplate(beats: Beat[], templates: string[]): Beat[] {
    return beats.filter((beat) => {
      return templates.some((template) => beat.template === template)
    })
  }
}
